import { NODE_ENV } from 'constants/environment';
import { FeatureFlag } from 'featureFlags';
import { getAppServices } from 'providers/app-services';
import { AppState, AppStateActionParams } from 'records/AppState';
import { Project } from 'records/Project';

export interface PendoActions {
  updatePendo: (actionParams: AppStateActionParams) => Promise<AppState>;
}

const DEFAULT_ACCOUNT_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Returns the project id if FeatureFlag.UseAccountForAnalytics is not enabled (false).
 * Otherwise returns the account id or DEFAULT_ACCOUNT_ID if undefined or empty.
 */
const chooseAccountId = (
  projectId: string,
  accountId: string | undefined,
  featureFlagService: Bluebeam.Services.IFeatureFlagService
): string => {
  if (!featureFlagService.getFeature(FeatureFlag.UseAccountForAnalytics)) {
    return projectId;
  }
  return accountId || DEFAULT_ACCOUNT_ID;
};

const pendoActions: PendoActions = {
  updatePendo: async ({ state }: AppStateActionParams): Promise<AppState> => {
    const { featureFlagService } = getAppServices();
    const isPendoFlagEnabled = featureFlagService.getFeature(FeatureFlag.Pendo);
    const isPendoDebuggingEnabled = featureFlagService.getFeature(
      FeatureFlag.PendoDebugging
    );
    const isPendoEnabled =
      NODE_ENV !== 'development' && pendo && !!isPendoFlagEnabled;

    // NOTE: 'pendo' is a global variable.
    if (isPendoEnabled) {
      const {
        projects,
        selectedProject,
        users,
        roles,
        eula,
        pendoInitialized,
        sheets,
        mappedSheetVersions,
        userLicense,
      } = state();
      const project = projects.get(selectedProject) || new Project();
      const { company } = eula;
      const { authService } = getAppServices();
      const { userId, email } = authService.getCredentials();
      const userRole = users.getIn([userId, 'profile', 'role']);
      const permLevel = roles.getIn([userId, 'role']);
      const creatorEmail = project.creatorUser.email;
      const sheetsCount = sheets.size;
      const languageCode = localStorage.getItem('BB_LANGUAGE_CODE');

      const pendoPayload = {
        visitor: {
          id: userId,
          PermissionLevel: permLevel,
          ...(userRole && userRole.length > 0 ? { UserRole: userRole } : {}),
          email,
          full_name: `${eula.firstname} ${eula.lastname}`, // eslint-disable-line
          ...(company && company.length > 0 ? { Company: company } : {}),
          userLanguage: languageCode,
          tier: userLicense?.package,
        },
        account: {
          id: chooseAccountId(
            project.id,
            userLicense?.accountId,
            featureFlagService
          ),
          salesforceId: userLicense?.salesforceId,
          // Both ProjectName AND name are needed here, even though they look redundant,
          // to help with a bug in Pendo Feedback.
          ProjectName: project.name,
          name: `${project.name} - ${project.creatorUser.email}`,
          ...(creatorEmail && creatorEmail.length > 0
            ? { CreatorEmail: creatorEmail }
            : {}),
          is_paying: false,
          monthly_value: 0,
          num_sheets_per_proj: sheetsCount,
          num_users_per_proj: users.size,
          num_map_layers: mappedSheetVersions.size,
        },
      };

      if (!pendoInitialized) {
        if (isPendoDebuggingEnabled) {
          console.log('WC: Initializing Pendo User as:', pendoPayload);
        }
        pendo.initialize(pendoPayload);
      }

      if (isPendoDebuggingEnabled) {
        // TODO: I'm pretty sure that this should be either initialize or identify, but not both.
        // This is a low priority fix, we can look into it later.  It shouldn't cause any problems with pendo...
        console.log('WC: Identifying Pendo User as:', pendoPayload);
      }
      pendo.identify(pendoPayload);
    }

    return state().set('pendoInitialized', true);
  },
};

export default pendoActions;
